import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  HiOutlineUpload, HiOutlineRefresh, HiOutlineClock,
  HiOutlineCheckCircle, HiOutlineExclamationCircle,
  HiOutlineRewind, HiOutlineServer, HiOutlineDocument,
  HiOutlineLockClosed, HiOutlineArrowRight, HiOutlineChat,
  HiOutlineStatusOnline
} from 'react-icons/hi';
import { DeploymentSkeleton } from '../components/Skeleton';
import ConfirmModal from '../components/ConfirmModal';

const API_BASE = process.env.REACT_APP_API_URL || '';
const MONTHS_FR = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS_FR[d.getMonth()]} ${d.getFullYear()} à ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

function formatSize(mb) {
  if (!mb && mb !== 0) return '—';
  if (mb < 1) return `${Math.round(mb * 1024)} Ko`;
  return `${mb} Mo`;
}

const APP_LABELS = {
  'ds5-backend': { label: 'Backend API', icon: '⚙️', color: 'var(--accent-blue)' },
  'ds5-frontend': { label: 'Frontend', icon: '🖥️', color: 'var(--accent-green)' },
  'ds5-suivi': { label: 'Suivi Web', icon: '📋', color: 'var(--accent-yellow)' },
};

function StepBadge({ status }) {
  if (status === 'ok') return <span className="badge healthy" style={{ fontSize: 11 }}>✓</span>;
  if (status === 'warn') return <span className="badge degraded" style={{ fontSize: 11 }}>⚠</span>;
  if (status === 'error') return <span className="badge down" style={{ fontSize: 11 }}>✗</span>;
  return <span className="badge loading" style={{ fontSize: 11 }}>…</span>;
}

// ─── PIN Input Modal ────────────────
function PinModal({ open, title, message, warning, onConfirm, onCancel, loading: extLoading, children }) {
  const [pin, setPin] = useState('');
  const [releaseNote, setReleaseNote] = useState('');
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1=confirm, 2=pin
  const prevOpen = useRef(false);

  // Reset all state when modal opens (fixes consecutive deploy bug)
  useEffect(() => {
    if (open && !prevOpen.current) {
      setPin('');
      setReleaseNote('');
      setError('');
      setStep(1);
    }
    prevOpen.current = open;
  }, [open]);

  if (!open) return null;

  const handleStep1 = () => setStep(2);
  const handleSubmit = () => {
    if (!pin || pin.length < 4) {
      setError('PIN requis (4 chiffres)');
      return;
    }
    setError('');
    onConfirm({ pin, releaseNote });
  };

  const handleCancel = () => {
    setPin('');
    setReleaseNote('');
    setError('');
    setStep(1);
    onCancel();
  };

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        {step === 1 ? (
          <>
            <h3>
              <HiOutlineExclamationCircle style={{ verticalAlign: 'middle', color: 'var(--accent-red)', marginRight: 8 }} />
              {title}
            </h3>
            <p>{message}</p>
            {warning && <div className="warning-text">⚠ {warning}</div>}

            {/* Slot for extra content (e.g. restore data checkbox) */}
            {children}

            <div style={{ marginTop: 16 }}>
              <label style={{ fontSize: 13, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
                <HiOutlineChat style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Release note (optionnel)
              </label>
              <textarea
                value={releaseNote}
                onChange={e => setReleaseNote(e.target.value)}
                placeholder="Décrivez les changements déployés..."
                style={{
                  width: '100%', minHeight: 60, padding: '8px 12px',
                  borderRadius: 8, border: '1px solid var(--border-color)',
                  background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                  fontSize: 13, resize: 'vertical', boxSizing: 'border-box',
                }}
              />
            </div>

            <div className="modal-actions">
              <button className="btn btn-outline" onClick={handleCancel}>Annuler</button>
              <button className="btn btn-danger" onClick={handleStep1}>
                Continuer → Saisir le PIN
              </button>
            </div>
          </>
        ) : (
          <>
            <h3>
              <HiOutlineLockClosed style={{ verticalAlign: 'middle', color: 'var(--accent-red)', marginRight: 8 }} />
              Confirmation PIN
            </h3>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Saisissez le PIN de déploiement pour confirmer l'action.
            </p>

            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <input
                type="password"
                inputMode="numeric"
                maxLength={8}
                value={pin}
                onChange={e => { setPin(e.target.value.replace(/\D/g, '')); setError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                placeholder="• • • •"
                autoFocus
                style={{
                  width: 160, padding: '12px 16px', fontSize: 24,
                  textAlign: 'center', letterSpacing: 8,
                  borderRadius: 8, border: `2px solid ${error ? 'var(--accent-red)' : 'var(--border-color)'}`,
                  background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                }}
              />
              {error && <div style={{ color: 'var(--accent-red)', fontSize: 12, marginTop: 8 }}>{error}</div>}
            </div>

            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setStep(1)}>← Retour</button>
              <button className="btn btn-danger" onClick={handleSubmit} disabled={extLoading}>
                {extLoading ? '⟳ Déploiement...' : '� Confirmer le déploiement'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ────────────────
export default function DeploymentPage({ token, stagingStatus, onNavigate }) {
  const [versions, setVersions] = useState(null);
  const [history, setHistory] = useState([]);
  const [backups, setBackups] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [message, setMessage] = useState(null);
  const [deployModal, setDeployModal] = useState(null); // { app, ... }
  const [rollbackModal, setRollbackModal] = useState(null); // { app, backup, hasDbDump, ... }
  const [restoreData, setRestoreData] = useState(false); // checkbox for DB restore
  const [deployResult, setDeployResult] = useState(null); // show steps after deploy
  const [activeTab, setActiveTab] = useState('deploy'); // 'deploy' | 'history' | 'rollback'

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const isStagingDown = stagingStatus === 'down';

  const fetchData = useCallback(async () => {
    try {
      // Only skip /api/deploy/versions when staging is down (SSHs to staging → 10s timeout)
      // History and backups are local to prod server → always fetch them
      const [versRes, histRes, bkpRes] = await Promise.all([
        isStagingDown ? null : fetch(`${API_BASE}/api/deploy/versions`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/deploy/history`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/deploy/backups`, { headers }).catch(() => null),
      ]);
      if (versRes?.ok) setVersions(await versRes.json());
      if (histRes?.ok) {
        const data = await histRes.json();
        setHistory(data.history || []);
      }
      if (bkpRes?.ok) {
        const data = await bkpRes.json();
        setBackups(data.backups || {});
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token, isStagingDown]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Deploy action ────────
  const handleDeploy = async ({ pin, releaseNote }) => {
    const app = deployModal?.app;
    if (!app) return;
    setActionLoading(app);
    setDeployResult(null);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/api/deploy/execute`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ pin, app, releaseNote }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: data.message });
        setDeployResult(data.entry);
      } else {
        setMessage({ type: 'error', text: data.error });
        if (data.entry) setDeployResult(data.entry);
      }
      setDeployModal(null);
      await fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading('');
    }
  };

  // ─── Rollback action ────────
  const handleRollback = async ({ pin }) => {
    const { app, backup } = rollbackModal || {};
    if (!app || !backup) return;
    setActionLoading(`rollback-${app}`);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/api/deploy/rollback`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ pin, app, backup, restoreData }),
      });
      const data = await res.json();
      setMessage({
        type: res.ok ? 'success' : 'error',
        text: res.ok ? data.message : data.error,
      });
      if (res.ok && data.entry) setDeployResult(data.entry);
      setRollbackModal(null);
      setRestoreData(false);
      await fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading('');
    }
  };

  if (loading) return <DeploymentSkeleton />;

  return (
    <div className="fade-in">
      <div className="page-title">
        <HiOutlineUpload className="title-icon" style={{ color: 'var(--accent-blue)' }} />
        Déploiement Production
      </div>

      {/* Message banner */}
      {message && (
        <div
          className="card"
          style={{
            marginBottom: 16,
            borderLeft: `4px solid ${message.type === 'success' ? 'var(--accent-green)' : 'var(--accent-red)'}`,
            padding: '12px 20px',
          }}
        >
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid var(--border-color)' }}>
        {[
          { id: 'deploy', label: 'Déployer', icon: <HiOutlineUpload /> },
          { id: 'history', label: 'Historique', icon: <HiOutlineClock /> },
          { id: 'rollback', label: 'Rollback', icon: <HiOutlineRewind /> },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px', cursor: 'pointer', border: 'none',
              background: activeTab === tab.id ? 'var(--bg-secondary)' : 'transparent',
              color: activeTab === tab.id ? 'var(--text-primary)' : 'var(--text-muted)',
              borderBottom: activeTab === tab.id ? '2px solid var(--accent-blue)' : '2px solid transparent',
              marginBottom: -2, fontSize: 14, fontWeight: activeTab === tab.id ? 600 : 400,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            {tab.icon} {tab.label}
            {tab.id === 'history' && history.length > 0 && (
              <span className="badge" style={{ fontSize: 10, marginLeft: 4, background: 'var(--bg-tertiary)', padding: '2px 6px', borderRadius: 8 }}>
                {history.length}
              </span>
            )}
          </button>
        ))}

        <div style={{ flex: 1 }} />
        <button
          className="btn btn-outline"
          onClick={() => { setLoading(true); fetchData(); }}
          style={{ alignSelf: 'center', marginBottom: 4, padding: '6px 12px', fontSize: 12 }}
        >
          <HiOutlineRefresh /> Actualiser
        </button>
      </div>

      {/* ─── Tab: Deploy ──── */}
      {activeTab === 'deploy' && (
        <>
          {/* Staging down banner — only affects deploy tab */}
          {isStagingDown && (
            <div className="card" style={{
              marginBottom: 16,
              borderLeft: '4px solid var(--accent-red)',
              padding: '16px 20px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <HiOutlineExclamationCircle style={{ color: 'var(--accent-red)', fontSize: 24, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Serveur staging arrêté</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    Le serveur staging est actuellement éteint. Le déploiement (staging → prod) n'est pas possible.
                  </div>
                </div>
                {onNavigate && (
                  <button
                    className="btn btn-primary"
                    onClick={() => onNavigate('staging')}
                    style={{ padding: '6px 14px', fontSize: 12, whiteSpace: 'nowrap' }}
                  >
                    <HiOutlineServer style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    Démarrer le serveur
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Version comparison cards — hidden when staging is down */}
          {!isStagingDown && (
          <div className="card" style={{ marginBottom: 24 }}>
            <div className="card-header">
              <div className="card-title"><HiOutlineServer className="icon" /> Comparaison des versions</div>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Application</th>
                    <th>Version Prod</th>
                    <th></th>
                    <th>Version Staging</th>
                    <th>Statut</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(versions?.apps || []).map(app => {
                    const meta = APP_LABELS[app.name] || { label: app.name, icon: '📦', color: 'var(--text-muted)' };
                    return (
                      <tr key={app.name}>
                        <td>
                          <span style={{ marginRight: 8 }}>{meta.icon}</span>
                          <strong>{meta.label}</strong>
                        </td>
                        <td>
                          <code style={{ padding: '2px 8px', borderRadius: 4, background: 'var(--bg-tertiary)', fontSize: 13 }}>
                            {app.prodVersion}
                          </code>
                        </td>
                        <td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                          <HiOutlineArrowRight />
                        </td>
                        <td>
                          <code style={{
                            padding: '2px 8px', borderRadius: 4, fontSize: 13,
                            background: app.isDifferent ? 'rgba(34,197,94,0.15)' : 'var(--bg-tertiary)',
                            color: app.isDifferent ? 'var(--accent-green)' : 'var(--text-primary)',
                          }}>
                            {app.stagingVersion}
                          </code>
                        </td>
                        <td>
                          {app.stagingVersion === 'offline' ? (
                            <span className="badge down" style={{ fontSize: 11 }}>Staging off</span>
                          ) : app.isDifferent ? (
                            <span className="badge degraded" style={{ fontSize: 11 }}>Diff</span>
                          ) : (
                            <span className="badge healthy" style={{ fontSize: 11 }}>Sync</span>
                          )}
                        </td>
                        <td>
                          <button
                            className={`btn ${app.isDifferent ? 'btn-primary' : 'btn-outline'}`}
                            disabled={app.stagingVersion === 'offline' || !!actionLoading}
                            onClick={() => setDeployModal({
                              app: app.name,
                              label: meta.label,
                              from: app.stagingVersion,
                              to: app.prodVersion,
                            })}
                            style={{
                              padding: '4px 14px', fontSize: 12,
                              opacity: (!app.isDifferent && !actionLoading) ? 0.6 : 1,
                            }}
                          >
                            {actionLoading === app.name
                              ? '⟳ …'
                              : app.isDifferent
                                ? `🚀 Déployer v${app.stagingVersion}`
                                : '↻ Redéployer'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
              <HiOutlineDocument style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Le dossier complet staging est copié en prod (.env prod préservé). Les URLs staging sont automatiquement remplacées.
            </div>
          </div>

          )}

          {/* Deploy result steps */}
          {deployResult && (
            <div className="card" style={{
              marginBottom: 24,
              borderLeft: `4px solid ${deployResult.status === 'success' ? 'var(--accent-green)' : 'var(--accent-red)'}`,
            }}>
              <div className="card-header">
                <div className="card-title">
                  {deployResult.status === 'success' ? <HiOutlineCheckCircle className="icon" style={{ color: 'var(--accent-green)' }} /> : <HiOutlineExclamationCircle className="icon" style={{ color: 'var(--accent-red)' }} />}
                  Résultat : {APP_LABELS[deployResult.app]?.label || deployResult.app}
                  {deployResult.type === 'rollback' ? ' — Rollback' : ''}
                </div>
                <span className={`badge ${deployResult.status === 'success' ? 'healthy' : 'down'}`} style={{ fontSize: 11 }}>
                  {deployResult.status === 'success' ? 'Succès' : 'Échec'}
                </span>
              </div>
              <div style={{ marginTop: 8, fontSize: 13 }}>
                {deployResult.previousVersion !== deployResult.newVersion && (
                  <div style={{ marginBottom: 8, color: 'var(--text-muted)' }}>
                    Version : <code>{deployResult.previousVersion}</code> → <code>{deployResult.newVersion}</code>
                  </div>
                )}
                {deployResult.releaseNote && (
                  <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: 'var(--bg-tertiary)', fontStyle: 'italic', fontSize: 12 }}>
                    📝 {deployResult.releaseNote}
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  {(deployResult.steps || []).map((s, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '4px 10px', borderRadius: 6,
                      background: 'var(--bg-tertiary)', fontSize: 11,
                    }}>
                      <StepBadge status={s.status} />
                      <span>{s.step}</span>
                      {s.detail && <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>({s.detail})</span>}
                    </div>
                  ))}
                </div>
              </div>
              <button
                className="btn btn-outline"
                onClick={() => setDeployResult(null)}
                style={{ marginTop: 12, padding: '4px 12px', fontSize: 11 }}
              >
                Fermer
              </button>
            </div>
          )}
        </>
      )}

      {/* ─── Tab: History ──── */}
      {activeTab === 'history' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title"><HiOutlineClock className="icon" /> Historique de déploiement</div>
            <span className="badge" style={{ fontSize: 11 }}>{history.length} entrées</span>
          </div>
          {history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              Aucun déploiement enregistré
            </div>
          ) : (
            <div style={{ maxHeight: 600, overflowY: 'auto' }}>
              {history.map((entry, idx) => {
                const meta = APP_LABELS[entry.app] || { label: entry.app, icon: '📦' };
                return (
                  <div
                    key={entry.id || idx}
                    style={{
                      padding: '14px 0',
                      borderBottom: idx < history.length - 1 ? '1px solid var(--border-color)' : 'none',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span>{meta.icon}</span>
                          <strong style={{ fontSize: 14 }}>{meta.label}</strong>
                          {entry.type === 'rollback' && (
                            <span className="badge degraded" style={{ fontSize: 10 }}>Rollback</span>
                          )}
                          <span className={`badge ${entry.status === 'success' ? 'healthy' : entry.status === 'failed' ? 'down' : 'loading'}`} style={{ fontSize: 10 }}>
                            {entry.status === 'success' ? '✓' : entry.status === 'failed' ? '✗' : '…'}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {formatDate(entry.timestamp)} — par <strong>{entry.user}</strong>
                          {entry.previousVersion && entry.newVersion && entry.previousVersion !== entry.newVersion && (
                            <span style={{ marginLeft: 8 }}>
                              <code style={{ fontSize: 11 }}>{entry.previousVersion}</code> → <code style={{ fontSize: 11 }}>{entry.newVersion}</code>
                            </span>
                          )}
                        </div>
                      </div>
                      {entry.steps && (
                        <div style={{ display: 'flex', gap: 2 }}>
                          {entry.steps.map((s, i) => <StepBadge key={i} status={s.status} />)}
                        </div>
                      )}
                    </div>
                    {entry.releaseNote && (
                      <div style={{
                        marginTop: 6, padding: '6px 10px',
                        borderRadius: 6, background: 'var(--bg-tertiary)',
                        fontSize: 12, fontStyle: 'italic', color: 'var(--text-secondary)',
                      }}>
                        📝 {entry.releaseNote}
                      </div>
                    )}
                    {entry.error && (
                      <div style={{ marginTop: 4, fontSize: 11, color: 'var(--accent-red)' }}>
                        Erreur : {entry.error}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ─── Tab: Rollback ──── */}
      {activeTab === 'rollback' && (
        <div>
          {Object.keys(APP_LABELS).map(appName => {
            const meta = APP_LABELS[appName];
            const appBackups = backups[appName] || [];
            const isBackend = appName === 'ds5-backend';
            return (
              <div className="card" key={appName} style={{ marginBottom: 16 }}>
                <div className="card-header">
                  <div className="card-title">
                    <span style={{ marginRight: 8 }}>{meta.icon}</span>
                    {meta.label}
                  </div>
                  <span className="badge" style={{ fontSize: 11 }}>{appBackups.length} backup{appBackups.length !== 1 ? 's' : ''}</span>
                </div>
                {appBackups.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>
                    Aucun backup de déploiement disponible
                  </div>
                ) : (
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Version</th>
                          <th>Taille</th>
                          {isBackend && <th>Dump BDD</th>}
                          <th>Note</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {appBackups.map((bk, i) => (
                          <tr key={bk.filename}>
                            <td style={{ fontSize: 13 }}>{formatDate(bk.date)}</td>
                            <td>
                              {bk.version ? (
                                <div>
                                  <code style={{ fontSize: 11, padding: '1px 6px', borderRadius: 4, background: 'var(--bg-tertiary)' }}>
                                    v{bk.version}
                                  </code>
                                  {bk.newVersion && bk.newVersion !== bk.version && (
                                    <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 4 }}>
                                      → v{bk.newVersion}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>
                              )}
                            </td>
                            <td style={{ fontSize: 13 }}>{formatSize(bk.sizeMB)}</td>
                            {isBackend && (
                              <td>
                                {bk.hasDbDump ? (
                                  <span className="badge healthy" style={{ fontSize: 10 }}>
                                    ✓ {formatSize(bk.dbDumpSizeMB)}
                                  </span>
                                ) : (
                                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>
                                )}
                              </td>
                            )}
                            <td style={{ fontSize: 11, color: 'var(--text-muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {bk.releaseNote || '—'}
                            </td>
                            <td>
                              <button
                                className="btn btn-outline"
                                disabled={!!actionLoading}
                                onClick={() => {
                                  setRestoreData(false);
                                  setRollbackModal({
                                    app: appName,
                                    backup: bk.filename,
                                    label: meta.label,
                                    date: formatDate(bk.date),
                                    version: bk.version,
                                    hasDbDump: bk.hasDbDump,
                                    dbDumpSizeMB: bk.dbDumpSizeMB,
                                    releaseNote: bk.releaseNote,
                                  });
                                }}
                                style={{ padding: '3px 10px', fontSize: 11 }}
                              >
                                {actionLoading === `rollback-${appName}` ? '⟳ …' : '↩ Restaurer'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
            <HiOutlineDocument style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Les backups sont créés automatiquement avant chaque déploiement. Un dump BDD est créé avant chaque déploiement du backend. Le .env prod est préservé lors du rollback.
          </div>
        </div>
      )}

      {/* ─── Deploy Modal (double confirmation + PIN) ──── */}
      <PinModal
        open={!!deployModal}
        title={deployModal?.from !== deployModal?.to
          ? `Déployer ${deployModal?.label} v${deployModal?.from} en production`
          : `Redéployer ${deployModal?.label} v${deployModal?.from} en production`}
        message={deployModal?.from !== deployModal?.to
          ? `Vous allez promouvoir la version staging (v${deployModal?.from}) vers la production (actuellement v${deployModal?.to}).`
          : `La version production est déjà en v${deployModal?.to}. Vous allez redéployer la même version depuis staging.`}
        warning="Cette action va mettre à jour l'application en production. Le dossier complet staging sera copié (bundle + config). Le .env prod sera préservé."
        onConfirm={handleDeploy}
        onCancel={() => setDeployModal(null)}
        loading={!!actionLoading}
      />

      {/* ─── Rollback Modal (PIN + checkbox restore data) ──── */}
      <PinModal
        open={!!rollbackModal}
        title={`Rollback ${rollbackModal?.label}${rollbackModal?.version ? ` → v${rollbackModal.version}` : ''}`}
        message={`Restaurer le backup du ${rollbackModal?.date}.${rollbackModal?.version ? ` Version : v${rollbackModal.version}.` : ''}`}
        warning="Cette action va remplacer l'application en production par une version précédente."
        onConfirm={handleRollback}
        onCancel={() => { setRollbackModal(null); setRestoreData(false); }}
        loading={!!actionLoading}
      >
        {/* Checkbox: restore DB data (only for backend with DB dump available) */}
        {rollbackModal?.app === 'ds5-backend' && (
          <div style={{
            marginTop: 16, padding: '10px 14px', borderRadius: 8,
            background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)',
          }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: rollbackModal?.hasDbDump ? 'pointer' : 'not-allowed' }}>
              <input
                type="checkbox"
                checked={restoreData}
                onChange={e => setRestoreData(e.target.checked)}
                disabled={!rollbackModal?.hasDbDump}
                style={{ marginTop: 3, accentColor: 'var(--accent-red)' }}
              />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: rollbackModal?.hasDbDump ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                  Restaurer aussi les données (BDD)
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  {rollbackModal?.hasDbDump
                    ? `Dump MySQL disponible (${formatSize(rollbackModal.dbDumpSizeMB)}). Le dump sera importé après la restauration des fichiers.`
                    : 'Aucun dump BDD disponible pour ce backup. Seuls les fichiers seront restaurés.'}
                </div>
                {restoreData && (
                  <div style={{ marginTop: 6, fontSize: 11, color: 'var(--accent-red)', fontWeight: 500 }}>
                    ⚠ La base de données actuelle sera écrasée par le dump de cette version.
                  </div>
                )}
              </div>
            </label>
          </div>
        )}
      </PinModal>
    </div>
  );
}
